import type { Store } from 'effector'
import type { TvChannelPayment } from '~/features/payment/interfaces'

import { combine, sample } from 'effector'
import { model as router } from '~/core/router'
import { selectEpgChannelModel } from '~/features/epg/channels/select'
import { getEpgPathFromSignInModal } from '~/features/epg/modals/signInToWatch/helpers'
import { model as paymentModel } from '~/features/payment'
import * as pinLockFeature from '~/features/pin-lock'
import { $isFullscreen } from '~/shared/effector/fullscreen'
import { aye } from '~/shared/helpers'
import { epgChannelsWidgetModel } from '~/widgets/epg/channels'
// page popup
import { model as pagePaymentPopup } from '~/widgets/Payment/ExternalPaymentOpen'
// player popup
import { genPlayerPaymentLogic } from '~/widgets/Player/PlayerPaymentPopup'
import { genSignInToWatchLogic } from '~/widgets/Player/PlayerSignInToWatchPopup'
import {
  model as pageSignInPopupModel,
  model as signInToWatchPopupModel,
} from '~/widgets/Popups/SignInToWatchPopup'
import { formatEpgTvChannelPayment } from '../helpers'
import { $epgActiveChannel, $isOnThePage, left, reset } from '../index'

const $epgTvChannelPaymentFormat: Store<TvChannelPayment | null> = combine(
  $epgActiveChannel,
  router.$query,
  (channel, query) => {
    if (!channel) return null

    return formatEpgTvChannelPayment(channel, query)
  }
)

// external payment popup
export const { proceedPurchaseClick, $showPaymentPopup } =
  genPlayerPaymentLogic({
    entityStore: $epgTvChannelPaymentFormat,
  })

export const $showPaymentPopupInPlayer: Store<boolean | null> = combine(
  epgChannelsWidgetModel.$isFirstAutoSelect,
  epgChannelsWidgetModel.$isFullscreenJustOff,
  $isFullscreen,
  $showPaymentPopup,
  (
    isFirstAutoSelect,
    isFullscreenJustOff,
    isFullscreen,
    isShowPaymentPopup
  ) => {
    if (isFullscreen || isFullscreenJustOff) return isShowPaymentPopup
    return isFirstAutoSelect ? isShowPaymentPopup : false
  }
)

// sign in to watch popup
const $signInToWatchRedirect = combine(router.$query, getEpgPathFromSignInModal)

export const { signInToWatchPopupSubmit, $showSignInToWatchPopup } =
  genSignInToWatchLogic({
    entityStore: selectEpgChannelModel.$active,
    redirectPath: $signInToWatchRedirect,
  })

// clear active channel on page leave
sample({
  clock: $isOnThePage,
  fn: () => null,
  target: selectEpgChannelModel.setActiveId,
})

// means common popups blocked from showing, in some cases on epg page only
// inside player popups still allowed
sample({
  clock: [
    epgChannelsWidgetModel.$isFirstAutoSelect,
    epgChannelsWidgetModel.$isFullscreenJustOff,
    $epgTvChannelPaymentFormat,
    $isOnThePage,
  ],
  source: {
    isFirstAutoSelect: epgChannelsWidgetModel.$isFirstAutoSelect,
    isFullscreenJustOff: epgChannelsWidgetModel.$isFullscreenJustOff,
    isOnThePage: $isOnThePage,
  },
  fn: ({ isOnThePage, isFirstAutoSelect, isFullscreenJustOff }) =>
    (aye(isFirstAutoSelect) || aye(isFullscreenJustOff)) && isOnThePage,
  target: [pagePaymentPopup.setBlocked, pageSignInPopupModel.setBlocked],
})

// Connections

sample({
  clock: epgChannelsWidgetModel.openPinlockPopup,
  fn: ({ id, name, formattedName, locked }) => ({
    ...pinLockFeature.emptyPinLockState,
    id,
    name: formattedName ?? name ?? '',
    locked,
  }),
  target: pinLockFeature.openPinLockPopup,
})

// close all modals when user leaves
sample({ clock: [left, reset], target: pinLockFeature.closePinLockPopup })
sample({
  clock: [left, reset],
  target: paymentModel.closeExternalPaymentPopup,
})
sample({
  clock: [left, reset],
  target: signInToWatchPopupModel.closeSignInToWatchPopup,
})
